// Local write path (spec §1 principle 1: saved locally before anything else).
// Every user write becomes an op in the ops table and is applied optimistically
// to the resources table in the same Dexie transaction. The sync engine later
// pushes ops and replaces local rows with the server's answer.
import { v7 as uuidv7 } from 'uuid'
import type { Db } from './schema'
import { META } from './schema'
import type { Edge, Op, OpRecord, Relation, Resource } from './types'
import { KindError, migrateSpec, type KindRegistry } from '@/kinds/kind'
import { nowISO } from '@/lib/time'
import { ALL, DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { ADDED_BY } from '@/lib/follow'
import { memberGrant } from '@/state/access'

/** reserved label naming a resource's workspace */
export const WS = 'ws'

export interface PatchInput {
  spec?: Record<string, unknown | null>
  labels?: Record<string, string | null>
}

export class Mutator {
  constructor(
    private db: Db,
    private kinds: KindRegistry,
    private onLocalOp: () => void = () => {},
    /** current workspace slug; `*` (Everything) falls back to the default */
    private scope: () => string = () => DEFAULT_WORKSPACE_SLUG,
  ) {}

  /** set while a group of writes is one user action, so undo takes them back together */
  private group?: string
  /** set while undo replays an inverse, so those ops are not themselves undo targets */
  private undoOf?: string

  /**
   * Run writes as one user action: moving a task between projects is two edge
   * ops, a conversion is several, and undo should take all of them back at once.
   */
  async grouped<T>(fn: () => Promise<T>): Promise<T> {
    if (this.group) return fn() // already inside one: keep the outer group
    this.group = uuidv7()
    try {
      return await fn()
    } finally {
      this.group = undefined
    }
  }

  /**
   * Writes inside `fn` are bookkeeping, not changes to the owner's things: a
   * receipt recording its own decision. Undo leaves them alone.
   */
  async bookkeeping<T>(fn: () => Promise<T>): Promise<T> {
    const was = this.noUndo
    this.noUndo = true
    try {
      return await fn()
    } finally {
      this.noUndo = was
    }
  }

  private noUndo?: boolean

  /** Mark every write made inside `fn` as the undo of `opId`. */
  async undoing<T>(opId: string, fn: () => Promise<T>): Promise<T> {
    this.undoOf = opId
    try {
      return await this.grouped(fn)
    } finally {
      this.undoOf = undefined
    }
  }

  /** The resource as it is now, kept on the op so undo can put it back. */
  private async snapshot(id: string): Promise<OpRecord['before']> {
    const r = await this.db.resources.get(id)
    if (!r) return undefined
    return {
      spec: structuredClone(r.spec),
      labels: { ...r.labels },
      status: structuredClone(r.status) as Record<string, unknown>,
    }
  }

  private async deviceId(): Promise<string> {
    let id = await this.db.getMeta<string>(META.deviceId)
    if (!id) {
      id = uuidv7()
      await this.db.setMeta(META.deviceId, id)
    }
    return id
  }

  private async commit(op: Op, summary: string, before?: OpRecord['before']): Promise<void> {
    const rec: OpRecord = {
      ...op,
      created_at: op.at,
      state: 'pending',
      summary,
      ...(before ? { before } : {}),
      ...(this.group ? { group: this.group } : {}),
      ...(this.undoOf ? { undo_of: this.undoOf } : {}),
      ...(this.noUndo ? { no_undo: true } : {}),
    }
    await this.db.transaction('rw', this.db.resources, this.db.edges, this.db.ops, this.db.meta, async () => {
      await applyOpLocally(this.db, this.kinds, op)
      await this.db.ops.put(rec)
    })
    this.onLocalOp()
  }

  async create(
    kind: string,
    spec: Record<string, unknown>,
    labels: Record<string, string> = {},
    id?: string,
  ): Promise<Resource> {
    const k = this.kinds.get(kind)
    const at = nowISO()
    const full = k.defaults(spec)
    const grant = memberGrant()
    if (kind === 'Task' && grant && !labels[ADDED_BY]) labels = { ...labels, [ADDED_BY]: grant }
    if (k.workspaceScoped && !labels[WS]) {
      const ws = this.scope()
      labels = { ...labels, [WS]: ws && ws !== ALL ? ws : DEFAULT_WORKSPACE_SLUG }
    }
    const resource: Resource = {
      id: id ?? uuidv7(),
      kind,
      version: k.version,
      name: '',
      labels,
      spec: full,
      status: {},
      created_at: at,
      updated_at: at,
      deleted_at: null,
      clocks: {},
    }
    for (const key of Object.keys(full)) resource.clocks[`spec.${key}`] = at
    for (const key of Object.keys(labels)) resource.clocks[`labels.${key}`] = at
    resource.name = k.displayName(resource)
    resource.status = k.initialStatus(resource)
    const errs = k.validate(resource, [])
    if (errs.length) throw new KindError('invalid', errs.join('; '))
    await this.commit(
      { op_id: uuidv7(), at, device_id: await this.deviceId(), type: 'create', resource },
      `Added ${kind.toLowerCase()} “${resource.name}”`,
    )
    return resource
  }

  async patch(id: string, patch: PatchInput): Promise<void> {
    const r = await this.db.resources.get(id)
    if (!r || r.deleted_at) throw new KindError('invalid', 'resource not found')
    const at = nowISO()
    const clocks: Record<string, string> = {}
    for (const key of Object.keys(patch.spec ?? {})) clocks[`spec.${key}`] = at
    for (const key of Object.keys(patch.labels ?? {})) clocks[`labels.${key}`] = at
    if (!Object.keys(clocks).length) return
    // validate the merged result before writing anything
    const k = this.kinds.get(r.kind)
    const merged = mergePatch(r, patch)
    merged.spec = k.defaults(merged.spec)
    const parsed = k.spec.safeParse(merged.spec)
    if (!parsed.success) throw new KindError('invalid', parsed.error.issues.map((i) => i.message).join('; '))
    const edges = await this.db.edges.where('from_id').equals(id).toArray()
    const errs = k.validate(merged, edges)
    if (errs.length) throw new KindError('invalid', errs.join('; '))
    const before = await this.snapshot(id)
    await this.commit(
      {
        op_id: uuidv7(),
        at,
        device_id: await this.deviceId(),
        type: 'patch',
        id,
        kind: r.kind,
        ...(patch.spec ? { spec: patch.spec } : {}),
        ...(patch.labels ? { labels: patch.labels } : {}),
        clocks,
      },
      `Edited “${k.displayName(merged) || r.name}”`,
      before,
    )
  }

  async remove(id: string): Promise<void> {
    const r = await this.db.resources.get(id)
    if (!r) return
    await this.commit(
      { op_id: uuidv7(), at: nowISO(), device_id: await this.deviceId(), type: 'delete', id, kind: r.kind },
      `Deleted “${r.name}”`,
      await this.snapshot(id),
    )
  }

  /** Bring back something deleted: the Trash, and undo of a delete. */
  async restore(id: string): Promise<void> {
    const r = await this.db.resources.get(id)
    if (!r || !r.deleted_at) return
    await this.commit(
      { op_id: uuidv7(), at: nowISO(), device_id: await this.deviceId(), type: 'restore', id, kind: r.kind },
      `Restored “${r.name}”`,
    )
  }

  async action(id: string, action: string, args?: unknown): Promise<void> {
    const r = await this.db.resources.get(id)
    if (!r || r.deleted_at) throw new KindError('invalid', 'resource not found')
    const k = this.kinds.get(r.kind)
    const at = nowISO()
    k.apply(r, action, args ?? {}, at) // throws early on conflict so the UI can tell the user
    const before = await this.snapshot(id)
    await this.commit(
      {
        op_id: uuidv7(),
        at,
        device_id: await this.deviceId(),
        type: 'action',
        id,
        kind: r.kind,
        action,
        ...(args !== undefined ? { args } : {}),
      },
      `${capitalize(action)} “${r.name}”`,
      before,
    )
  }

  async setEdge(from_id: string, to_id: string, relation: Relation, deleted = false): Promise<void> {
    const at = nowISO()
    const edge: Edge = { from_id, to_id, relation, created_at: at, deleted_at: deleted ? at : null }
    if (!deleted) {
      const from = await this.db.resources.get(from_id)
      const to = await this.db.resources.get(to_id)
      if (!from || !to) throw new KindError('invalid', 'edge endpoint not found')
      if (relation === 'belongs_to' && from.kind === 'Task' && to.kind !== 'Project')
        throw new KindError('invalid', 'a task can only belong to a Project')
      if (relation === 'belongs_to' && from.labels[WS] && to.labels[WS] && from.labels[WS] !== to.labels[WS])
        throw new KindError('invalid', 'that project is in another suite')
    }
    await this.commit(
      { op_id: uuidv7(), at, device_id: await this.deviceId(), type: 'edge', edge, deleted },
      deleted ? 'Unlinked' : 'Linked',
    )
  }

  /** Move a task to a project (or none): drops the old belongs_to, adds the new one. */
  async setProject(taskId: string, projectId: string | null): Promise<void> {
    return this.grouped(() => this.setProjectInner(taskId, projectId))
  }

  private async setProjectInner(taskId: string, projectId: string | null): Promise<void> {
    const existing = await this.db.edges.where('from_id').equals(taskId).toArray()
    for (const e of existing) {
      if (e.relation === 'belongs_to' && !e.deleted_at && e.to_id !== projectId)
        await this.setEdge(e.from_id, e.to_id, 'belongs_to', true)
    }
    if (
      projectId &&
      !existing.some((e) => e.relation === 'belongs_to' && !e.deleted_at && e.to_id === projectId)
    )
      await this.setEdge(taskId, projectId, 'belongs_to')
  }
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export const mergePatch = (r: Resource, patch: PatchInput): Resource => {
  const spec: Record<string, unknown> = { ...r.spec }
  for (const [k, v] of Object.entries(patch.spec ?? {})) {
    if (v === null || v === undefined) delete spec[k]
    else spec[k] = v
  }
  const labels: Record<string, string> = { ...r.labels }
  for (const [k, v] of Object.entries(patch.labels ?? {})) {
    if (v === null || v === undefined) delete labels[k]
    else labels[k] = v
  }
  return { ...r, spec, labels }
}

/**
 * Apply an op to the local tables. Used both for the optimistic write and when
 * re-applying pending ops on top of freshly pulled server rows. It never
 * throws for state conflicts: the server is the authority and will reject.
 */
export const applyOpLocally = async (db: Db, kinds: KindRegistry, op: Op): Promise<void> => {
  switch (op.type) {
    case 'create': {
      const existing = await db.resources.get(op.resource.id)
      if (existing) return
      await db.resources.put(op.resource)
      return
    }
    case 'patch': {
      const r = await db.resources.get(op.id)
      if (!r || r.deleted_at) return
      const k = kinds.get(r.kind)
      let merged = mergePatch(r, { spec: op.spec, labels: op.labels })
      merged.spec = k.defaults(merged.spec)
      merged.clocks = { ...merged.clocks, ...op.clocks }
      merged.name = k.displayName(merged)
      merged.updated_at = op.at
      if (k.afterPatch) merged = k.afterPatch(merged, op.at)
      await db.resources.put(merged)
      return
    }
    case 'delete': {
      const r = await db.resources.get(op.id)
      if (!r) return
      await db.resources.put({ ...r, deleted_at: op.at, updated_at: op.at })
      for (const e of await db.edges.where('from_id').equals(op.id).toArray())
        if (!e.deleted_at) await db.edges.put({ ...e, deleted_at: op.at })
      for (const e of await db.edges.where('to_id').equals(op.id).toArray())
        if (!e.deleted_at) await db.edges.put({ ...e, deleted_at: op.at })
      return
    }
    case 'restore': {
      const r = await db.resources.get(op.id)
      if (!r || !r.deleted_at) return
      const deletedAt = r.deleted_at
      await db.resources.put({ ...r, deleted_at: null, updated_at: op.at })
      // only the edges this delete took with it, and only to things that are still here
      for (const [field, other] of [
        ['from_id', 'to_id'],
        ['to_id', 'from_id'],
      ] as const)
        for (const e of await db.edges.where(field).equals(op.id).toArray()) {
          if (e.deleted_at !== deletedAt) continue
          const end = await db.resources.get(e[other])
          if (!end || end.deleted_at) continue
          await db.edges.put({ ...e, deleted_at: null })
        }
      return
    }
    case 'action': {
      const r = await db.resources.get(op.id)
      if (!r || r.deleted_at) return
      const k = kinds.get(r.kind)
      try {
        const out = k.apply(r, op.action, op.args ?? {}, op.at)
        out.name = k.displayName(out)
        out.updated_at = op.at
        await db.resources.put(out)
      } catch (e) {
        if (!(e instanceof KindError)) throw e
      }
      return
    }
    case 'edge': {
      await db.edges.put({ ...op.edge, deleted_at: op.deleted ? (op.edge.deleted_at ?? op.at) : null })
      return
    }
  }
}

/** Bring an old local row up to the kind's current version (spec §4.4, client side). */
export const migrateLocal = (kinds: KindRegistry, r: Resource): Resource => {
  if (!kinds.has(r.kind)) return r
  const k = kinds.get(r.kind)
  if (r.version >= k.version) return r
  const spec = migrateSpec(k, r.version, r.spec)
  const out = { ...r, spec, version: k.version }
  out.name = k.displayName(out)
  return out
}
